// Rika's grower packout sheet — the arithmetic, pure and tested.
//
// Finance hand-builds a per-grower packout sheet in Excel because the app
// could not reproduce her numbers. The gap was never the tables; it was the
// DENOMINATOR. This module owns both definitions side by side so neither can
// be shown unlabelled again.
//
// ── The two packout figures ──────────────────────────────────────────────
//
//   packoutOfMassOutPct   packed ÷ (packed + rejects + waste)   GROWER-FACING
//   packoutOfTippedPct    packed ÷ tipped                       INTERNAL OPS
//
// They answer different questions and BOTH are legitimate:
//   - Rika's asks "of everything that came out of the packhouse, what
//     share was packable fruit?" — a grading question.
//   - packed÷tipped asks "of everything that went in, what share came out
//     packed?" — a yield question that also carries the packhouse's mass
//     loss (drench/wax water gain, dust, unrecorded shrink).
//
// WHY RIKA'S IS THE BETTER GROWER-FACING ONE: every term in her ratio is a
// packhouse-MEASURED OUTPUT off the same scales, so the figure
// self-reconciles and can never exceed 100% — the numerator is a subset of
// the denominator by construction. packed÷tipped mixes an output against an
// input captured on a different path, and when the tipped side is token or
// under-recorded it does not merely look odd: it has genuinely crossed
// 1000% and overflowed runs.packout_percent's DECIMAL(5,2), failing the
// PALLET INSERT itself 7× in the 14 days to 2026-08-19. Migration
// 20260916104332_clamp_run_packout_percent.sql exists only because of that.
// A ratio that can take an operator's capture down is not the one to hand a
// farmer.
//
// ── Which "packed" ───────────────────────────────────────────────────────
//
// Two different "packed kg" exist in this app and the grower report used to
// print BOTH under an identical "Packed kg" header:
//   (a) runs.packed_weight_kg   — SUM of the run's leaf pallets, regardless
//                                 of dispatch status.
//   (b) SUM(pallet_dispatch_lines.net_mass_kg) filtered to DISPATCHED.
//
// This module uses (a) — ALL packed fruit, dispatched or not (Thomas's
// call, 2026-08-28). Rika has NOT yet confirmed which one her workbook
// uses, so the sheet STATES the assumption rather than implying agreement.
// Numerator and denominator both come from (a) so the sheet self-
// reconciles; (b) still drives the carton/dispatch tables on the page under
// its own distinct "Dispatched kg" label.
//
// ── The honesty trap ─────────────────────────────────────────────────────
//
// runs.reject_weight_kg and runs.waste_weight_kg are NOT NULL DEFAULT 0
// (mig 001 / mig 307) and reject capture is entirely OPTIONAL: nothing
// gates run completion on it, and the offline path refuses to queue a
// reject without an already-open reject_bin_id (minting a new bin needs the
// network) — so on bad signal rejects silently never get logged at all.
//
// A stored 0 therefore means EITHER "genuinely no rejects" OR "nobody
// captured any", and the two are indistinguishable in the column. That is
// not a cosmetic problem here: a missing reject SHRINKS Rika's denominator,
// so an uncaptured run inflates her packout — and a run with no rejects and
// no waste computes to exactly 100.0%, the most flattering number on the
// sheet, produced by the absence of data. So we classify capture instead of
// printing a 0 we cannot stand behind (house convention: an absent value is
// an em-dash, never a zero — see PalletReceiptReport.tsx, drainReadings.ts,
// estimates/marginals.ts).

/** Run-level projection this module needs. Field names mirror `runs`. */
export interface PackoutRun {
  run_id?:              string
  variety:              string | null
  /** 'PACK' | 'EXTERNAL_RECEIPT' (mig 20260807090000, NOT NULL DEFAULT 'PACK'). */
  run_kind?:            string | null
  tipped_net_weight_kg: number | null
  packed_weight_kg:     number | null
  reject_weight_kg:     number | null
  waste_weight_kg:      number | null
}

/** Cartons packed per variety, from the dispatch-line side of the page. */
export type CartonsByVariety = ReadonlyMap<string, number>

/** run_kind for third-party fruit we did NOT pack (mig 20260807090000).
 *  Module-private — `isExternalReceipt` is the API; callers should ask the
 *  question rather than re-implement the comparison. */
const EXTERNAL_RECEIPT = 'EXTERNAL_RECEIPT'

/** Variety label for a run whose variety is null — matches the page's own
 *  bucket key so the packout rows line up with every other table. */
export const VARIETY_UNKNOWN = '—'

/**
 * Mass (kg) above which "reject + waste = 0" stops being believable.
 *
 * A citrus bin runs ~400 kg. A quarter-tonne of fruit through the line
 * without ONE kilogram of reject or waste logged is far more likely to be a
 * run nobody captured than a genuinely flawless one — real graded fruit
 * always sheds something. Below the floor we say nothing: a 30 kg sample
 * run with no rejects is entirely plausible, and crying "not captured" at
 * it would train operators to ignore the flag.
 */
export const CAPTURE_EVIDENCE_FLOOR_KG = 250

/** Per-run capture verdict for rejects + waste. */
export type RejectCapture =
  /** At least one reject or waste kg is on the record. */
  | 'CAPTURED'
  /** Both are 0 on a run that moved real fruit — we cannot tell "none" from
   *  "nobody logged any", so we refuse to call it zero. */
  | 'NOT_CAPTURED'
  /** Both are 0 but the run moved almost nothing; zero is plausible. */
  | 'TRIVIAL'

/** Rolled-up capture state for a group of runs (a variety, or the sheet). */
export type GroupCapture =
  /** Every contributing run has reject/waste evidence (or is trivial). */
  | 'CAPTURED'
  /** Some runs are uncaptured — the figures below are an UPPER BOUND:
   *  the missing rejects would only enlarge the denominator. */
  | 'PARTIAL'
  /** No contributing run has any evidence at all. The denominator is
   *  entirely unmeasured, so we publish no packout figure. */
  | 'NONE'

export interface VarietyPackout {
  variety:  string
  runCount: number
  /** runs.packed_weight_kg — ALL packed fruit, dispatched or not. */
  packedKg: number
  rejectKg: number
  wasteKg:  number
  /** runs.tipped_net_weight_kg — the INPUT side, for the ops figure only. */
  tippedKg: number
  /** Rika's "total": packed + rejects + waste. NOT tipped_net_weight_kg. */
  totalMassOutKg: number
  /** Cartons packed for this variety (dispatch-line side). */
  cartons: number
  /** GROWER-FACING. packed ÷ (packed + rejects + waste) × 100.
   *  null when the denominator is 0 or wholly uncaptured. ≤ 100 always. */
  packoutOfMassOutPct: number | null
  /** INTERNAL OPS. packed ÷ tipped × 100. null when nothing was tipped.
   *  Unbounded — this is the one that has crossed 1000%. */
  packoutOfTippedPct: number | null
  capture: GroupCapture
  /** How many contributing runs came back NOT_CAPTURED. */
  notCapturedRunCount: number
}

export interface PackoutSheet {
  varieties: VarietyPackout[]
  /** Same shape as a variety row, summed across all of them. */
  total: Omit<VarietyPackout, 'variety'>
  /** True when ANY row is PARTIAL or NONE — the sheet needs its caveat. */
  hasUncapturedRuns: boolean
}

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

/** Coerce a possibly-null numeric column to a finite number. NaN and null
 *  both become 0 — a garbage weight must not poison a sum silently. */
function num(v: number | string | null | undefined): number {
  const n = Number(v ?? 0)
  return Number.isFinite(n) ? n : 0
}

/** kg → tonnes. Rika's sheet is in tonnes; the app stores kg. */
export function toTonnes(kg: number): number {
  return kg / 1000
}

/** Tonnes for display, 2 dp — the resolution her workbook uses (165.74 t). */
export function fmtTonnes(kg: number | null | undefined): string {
  if (kg == null || !Number.isFinite(kg)) return '—'
  return toTonnes(kg).toFixed(2)
}

/** Percent for display, 1 dp, em-dash when we have no honest answer. */
export function fmtPct(p: number | null | undefined, decimals = 1): string {
  if (p == null || !Number.isFinite(p)) return '—'
  return `${p.toFixed(decimals)}%`
}

/** True for the third-party receipts that carry packed mass with zero tipped
 *  mass. They are not our packing and belong in no packout figure. Runs
 *  arriving from the page's query are already filtered server-side; this
 *  keeps the lib correct for any caller and pins the rule in one place. */
export function isExternalReceipt(r: Pick<PackoutRun, 'run_kind'>): boolean {
  return (r.run_kind ?? '').toUpperCase() === EXTERNAL_RECEIPT
}

/**
 * Rika's packout: packed ÷ (packed + rejects + waste), as a percentage.
 *
 * Returns null when nothing came out at all — 0/0 is not 0%, and a report
 * that prints 0% for "no production" is a report that gets a farmer angry
 * about a number that was never measured.
 */
export function packoutOfMassOut(
  packedKg: number, rejectKg: number, wasteKg: number,
): number | null {
  const out = packedKg + rejectKg + wasteKg
  if (!(out > 0)) return null
  return (packedKg / out) * 100
}

/**
 * The internal ops packout: packed ÷ tipped. Kept deliberately — it is the
 * mass-balance figure the existing banner reads against, and it is the only
 * one of the two that can expose a missed tipped-weight capture (by going
 * over 100%). Not the one we put in front of a grower.
 */
export function packoutOfTipped(packedKg: number, tippedKg: number): number | null {
  if (!(tippedKg > 0)) return null
  return (packedKg / tippedKg) * 100
}

/**
 * Per-run reject/waste capture verdict. See "the honesty trap" above.
 *
 * The mass we weigh the evidence against is the LARGER of tipped and packed:
 * either one being substantial means real fruit moved through the run, and
 * a run can have a token tipped weight (the mig-20260916104332 failure mode)
 * while packing tonnes.
 */
export function rejectCapture(r: PackoutRun): RejectCapture {
  const rejects = num(r.reject_weight_kg) + num(r.waste_weight_kg)
  if (rejects > 0) return 'CAPTURED'
  const moved = Math.max(num(r.tipped_net_weight_kg), num(r.packed_weight_kg))
  return moved >= CAPTURE_EVIDENCE_FLOOR_KG ? 'NOT_CAPTURED' : 'TRIVIAL'
}

/** Roll per-run verdicts into the group verdict. A group with no runs that
 *  could have carried evidence is CAPTURED (nothing is being hidden). */
export function groupCapture(verdicts: readonly RejectCapture[]): GroupCapture {
  const notCaptured = verdicts.filter(v => v === 'NOT_CAPTURED').length
  if (notCaptured === 0) return 'CAPTURED'
  // NONE only when not one run in the group has evidence — that is the case
  // where the packout would compute to a flattering 100.0% purely from
  // absent data, so it is the case where we publish no figure at all.
  return verdicts.some(v => v === 'CAPTURED') ? 'PARTIAL' : 'NONE'
}

// ---------------------------------------------------------------------------
// The sheet
// ---------------------------------------------------------------------------

interface Acc {
  variety: string
  runCount: number
  packedKg: number
  rejectKg: number
  wasteKg: number
  tippedKg: number
  verdicts: RejectCapture[]
}

function emptyAcc(variety: string): Acc {
  return { variety, runCount: 0, packedKg: 0, rejectKg: 0, wasteKg: 0, tippedKg: 0, verdicts: [] }
}

/** Close an accumulator into a row. Returns everything BUT the variety, so
 *  the same function serves the per-variety rows and the varietyless total. */
function finish(a: Acc, cartons: number): Omit<VarietyPackout, 'variety'> {
  const capture        = groupCapture(a.verdicts)
  const totalMassOutKg = a.packedKg + a.rejectKg + a.wasteKg
  return {
    runCount: a.runCount,
    packedKg: a.packedKg,
    rejectKg: a.rejectKg,
    wasteKg:  a.wasteKg,
    tippedKg: a.tippedKg,
    totalMassOutKg,
    cartons,
    // A wholly-uncaptured group's denominator is just its own numerator, so
    // the ratio would read 100.0% — the honest answer is that we do not know.
    packoutOfMassOutPct: capture === 'NONE'
      ? null
      : packoutOfMassOut(a.packedKg, a.rejectKg, a.wasteKg),
    packoutOfTippedPct:  packoutOfTipped(a.packedKg, a.tippedKg),
    capture,
    notCapturedRunCount: a.verdicts.filter(v => v === 'NOT_CAPTURED').length,
  }
}

/**
 * Build the packout sheet from run rows (+ per-variety carton counts from
 * the dispatch-line side of the page).
 *
 * EXTERNAL_RECEIPT runs are dropped here as well as in the query: they carry
 * packed mass with no tipped mass, so leaving them in would credit the
 * grower with fruit the packhouse never packed and quietly lift both
 * packout figures.
 *
 * Rows come back sorted by total mass out, descending — the biggest variety
 * reads first, which is the order Rika's sheet is laid out in.
 */
export function buildPackoutSheet(
  runs: readonly PackoutRun[],
  cartonsByVariety: CartonsByVariety = new Map(),
): PackoutSheet {
  const byVariety = new Map<string, Acc>()
  const totalAcc  = emptyAcc('')

  for (const r of runs) {
    if (isExternalReceipt(r)) continue
    const key = r.variety ?? VARIETY_UNKNOWN
    let acc = byVariety.get(key)
    if (!acc) { acc = emptyAcc(key); byVariety.set(key, acc) }

    const packed  = num(r.packed_weight_kg)
    const reject  = num(r.reject_weight_kg)
    const waste   = num(r.waste_weight_kg)
    const tipped  = num(r.tipped_net_weight_kg)
    const verdict = rejectCapture(r)

    for (const a of [acc, totalAcc]) {
      a.runCount += 1
      a.packedKg += packed
      a.rejectKg += reject
      a.wasteKg  += waste
      a.tippedKg += tipped
      a.verdicts.push(verdict)
    }
  }

  const varieties: VarietyPackout[] = Array.from(byVariety.values())
    .map(a => ({ variety: a.variety, ...finish(a, cartonsByVariety.get(a.variety) ?? 0) }))
    .sort((a, b) => b.totalMassOutKg - a.totalMassOutKg || a.variety.localeCompare(b.variety))

  // The total's cartons sum the varieties we actually kept, so a carton map
  // carrying an EXTERNAL_RECEIPT-only variety cannot inflate the footer.
  const totalCartons = varieties.reduce((s, v) => s + v.cartons, 0)

  return {
    varieties,
    total: finish(totalAcc, totalCartons),
    hasUncapturedRuns: varieties.some(v => v.capture !== 'CAPTURED'),
  }
}

/**
 * Sheet-level footnote text for the capture caveat, or null when every run
 * has evidence. Lives here so the wording is tested alongside the rule it
 * describes rather than drifting in JSX.
 */
export function captureCaveat(sheet: PackoutSheet): string | null {
  const runs = sheet.total.notCapturedRunCount
  if (runs === 0) return null
  return `${runs} run${runs === 1 ? '' : 's'} logged no rejects and no waste at all. `
    + 'Those are shown as "not captured", not as zero — reject capture is optional, '
    + 'so a blank is not proof of a clean run. Where a variety has some uncaptured runs '
    + 'its packout is an upper bound; where it has no captured run at all, no packout is shown.'
}
